import React from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Download, Loader2, Scale } from 'lucide-react';

import { PRIMARY, SECONDARY, Steps, TaskCard } from './TaskCard';
import { useSayPoaDone } from '../../../../services/onboarding';

// Remove your old POA (Nic 2026-10-27): a previous VSO, claims agent or
// attorney still holds power of attorney, and their specialist marked it on
// the call. A task card in "Your part": short steps, the prefilled VA Form
// 21-4138, and "I've done it"; their specialist confirms it (or sends it
// back). Never locked by VA.gov: removing a POA is a phone call and a VA site.
//
// `poa` is the server's my-onboarding.poa:
//   stage           needed | sent_back | client_done (the card shows for
//                   these; unasked, not_needed and confirmed hide it)
//   hotline         the VA line to call
//   formUrl         the prefilled form (a public file; `download`, since it's
//                   an XFA form most browsers won't render)
//   quickSubmitUrl  VA QuickSubmit
const SHOWS = ['needed', 'sent_back', 'client_done'];
export const poaCardShows = (poa) => Boolean(poa && SHOWS.includes(poa.stage));

const QUIET_LINK =
  'inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 no-underline hover:text-slate-900 hover:underline dark:text-slate-300 dark:hover:text-white';

const PoaCard = ({ poa, specialistName, n = 1, current = true }) => {
  const queryClient = useQueryClient();
  const sayDone = useSayPoaDone({ onSuccess: () => queryClient.invalidateQueries(['my-onboarding']) });
  const who = specialistName || 'Your specialist';
  const done = poa.stage === 'client_done';
  const steps = [
    <>
      Call the VA at <span className="font-semibold text-slate-900 dark:text-white">{poa.hotline}</span> and tell
      them you want to revoke your current POA.
    </>,
    'Download the form below and fill in your details. The statement is already written.',
    <>
      Submit it through{' '}
      <a
        href={poa.quickSubmitUrl}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-violet-700 underline underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
      >
        VA QuickSubmit
      </a>
      .
    </>,
    "Tell the previous organization you're no longer working with them, and cancel anything you signed with them.",
  ];
  return (
    <TaskCard
      n={n}
      tone="violet"
      icon={Scale}
      title="Remove your old POA"
      why="A previous organization still has power of attorney for your VA claims. We need it removed before we can file for you. It takes a few minutes."
      status={done ? 'waiting' : poa.stage === 'sent_back' ? 'sentBack' : 'todo'}
      // Checked with their specialist and not done yet.
      note={poa.stage === 'sent_back' ? "On your call this wasn't done yet. Once it is, tap I've done it and we'll pick right back up." : null}
      footer="If the form won't open in your browser, open it in Adobe Reader."
      current={current}
      done={done ? `You said it's removed. ${who} will confirm.` : null}
      // The form stays a tap away in case they need it again.
      aside={
        done ? (
          <a href={poa.formUrl} download className={QUIET_LINK}>
            <Download size={15} /> Download the form
          </a>
        ) : null
      }
      testId="poa-card"
    >
      <Steps steps={steps} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <a href={poa.formUrl} download className={PRIMARY}>
          <Download size={16} /> Download the form
        </a>
        <button
          type="button"
          disabled={sayDone.isLoading}
          onClick={() =>
            sayDone.mutate(undefined, {
              onSuccess: () => toast.success(`Thanks! ${who} will confirm it.`),
              onError: (e) => toast.error(e?.response?.data?.msg || "That didn't go through."),
            })
          }
          className={SECONDARY}
        >
          {sayDone.isLoading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} I&apos;ve done it
        </button>
      </div>
    </TaskCard>
  );
};

export default PoaCard;
